// src/pages/CancelRenew.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./Renew.css"; // keep same styling as cancel-redeem
import useEscapeToGoBack from "../hooks/useEscapeToGoBack";

export default function CancelRenew() {
  const navigate = useNavigate();
  useEscapeToGoBack();

  // ---- refs for focus / enter navigation ----
  const mmyyRef = useRef(null);
  const receiptRef = useRef(null);

  // ---- state ----
  const [mmyy, setMmyy] = useState("");
  const [receiptTail, setReceiptTail] = useState("");

  const [view, setView] = useState(null); // server preview (CancelRenewView)
  const [error, setError] = useState("");

  const [confirm1, setConfirm1] = useState(null);
  const [confirm2, setConfirm2] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ---- helpers ----
  const onlyDigits = (s = "") => String(s).replace(/\D/g, "");
  const pad4 = (s = "") => onlyDigits(s).padStart(4, "0");
  const detectSeries = (tailDigits) => (tailDigits.length === 5 ? "S" : "B");

  const canonicalOldReceipt = () => {
    const mmYY = pad4(mmyy);
    const tail = onlyDigits(receiptTail);
    const series = detectSeries(tail);
    const tailNorm = series === "B" ? tail.padStart(4, "0") : tail; // S keep 5 digits
    return `${series}/${mmYY}/${tailNorm}`;
  };

  const fmtDate = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = d.getFullYear();
    return `${dd}/${mm}/${yy}`;
  };

  // ---- focus behavior ----
  useEffect(() => {
    mmyyRef.current?.focus();
  }, []);

  const onMmyyKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      receiptRef.current?.focus();
    }
  };

  const onReceiptKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  // ---- actions ----
  const handleSearch = async () => {
    setError("");
    setView(null);
    setConfirm1(null);
    setConfirm2(null);

    if (!mmyy || !receiptTail) {
      setError("Please fill MMYY and Receipt Number.");
      return;
    }

    try {
      const res = await axios.get(
        "https://www.lianfoong.com/api/renewals/payments/lookup-cancel-renew",
        {
          params: { mmyy: pad4(mmyy), receiptNoTail: onlyDigits(receiptTail) },
        }
      );
      setView(res.data);
    } catch (e) {
      setError(e?.response?.data?.message || "Renewal payment not found!");
    }
  };

  const onFirstConfirm = (ans) => {
    setConfirm1(ans === "yes");
    if (ans !== "yes") setConfirm2(false);
  };

  const onSecondConfirm = async (ans) => {
    setConfirm2(ans === "yes");
    if (ans !== "yes") return;
    try {
      setIsSubmitting(true);
      const oldReceipt = view?.receiptNo || canonicalOldReceipt();
      await axios.delete(
        "https://www.lianfoong.com/api/renewals/payments/cancel-renew",
        { params: { receiptNo: oldReceipt } }
      );
      alert("Renewal cancelled successfully.");
      navigate("/dashboard");
    } catch (e) {
      alert(e?.response?.data?.message || "Cancellation failed.");
      setIsSubmitting(false);
    }
  };

  // ---- UI ----
  return (
    <div className="renew-container">
      <h2 className="title">Cancel – Renew(By Old Receipt)</h2>

      <div className="form-group">
        <label>Enter Month and Year (MMYY):</label>
        <input
          ref={mmyyRef}
          value={mmyy}
          onChange={(e) => setMmyy(onlyDigits(e.target.value))}
          onKeyDown={onMmyyKeyDown}
          placeholder="MMYY"
          inputMode="numeric"
          autoComplete="off"
        />
      </div>

      <div className="form-group">
        <label>Enter Receipt Number:</label>
        <input
          ref={receiptRef}
          value={receiptTail}
          onChange={(e) => setReceiptTail(onlyDigits(e.target.value))}
          onKeyDown={onReceiptKeyDown}
          placeholder="0001 or 80001"
          inputMode="numeric"
          autoComplete="off"
        />
      </div>

      <button className="search-btn" type="button" onClick={handleSearch}>
        Search
      </button>
      {error && <p className="error-message">{error}</p>}

      {view && (
        <div className="transaction-details">
          <div className="details-grid">
            <p>
              <strong>Old Receipt:</strong> {view.receiptNo}
            </p>
            <p>
              <strong>Renewal Payment:</strong> $
              {Number(view.paymentAmount).toFixed(2)}
            </p>
            <p>
              <strong>Paid On:</strong> {fmtDate(view.paymentDateTime)}
            </p>
            <p>
              <strong>Source:</strong> {view.renewalSource || "-"}
            </p>
            <p>
              <strong>New Receipt:</strong> {view.newReceiptNo || "-"}
            </p>

            {view.newTxn && (
              <>
                <p>
                  <strong>New Amount:</strong> $
                  {Number(view.newTxn.amount).toFixed(2)}
                </p>
                <p>
                  <strong>New Grant:</strong> {fmtDate(view.newTxn.grantDate)}
                </p>
                <p>
                  <strong>New Expiry:</strong> {fmtDate(view.newTxn.expiryDate)}
                </p>
                <p>
                  <strong>Items:</strong> {view.newTxn.item}
                </p>
                <p>
                  <strong>Weight:</strong> {view.newTxn.weight}
                </p>
              </>
            )}
          </div>

          {confirm1 === null && (
            <div className="alert-box">
              <p>
                <strong>Are you sure you want to cancel this renewal?</strong>
              </p>
              <button onClick={() => onFirstConfirm("yes")}>Yes</button>
              <button onClick={() => onFirstConfirm("no")}>No</button>
            </div>
          )}

          {confirm1 === true && confirm2 === null && (
            <div className="alert-box">
              <p>
                <strong>Please re-confirm:</strong>
              </p>
              <button disabled={isSubmitting} onClick={() => onSecondConfirm("yes")}>
                {isSubmitting ? "Cancelling..." : "Yes"}
              </button>
              <button disabled={isSubmitting} onClick={() => onSecondConfirm("no")}>
                No
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
